import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Github, Lock, Mail, Key, Eye, Copy, Smartphone } from 'lucide-react';
import './SoulProject.css';

function OtpProject() {
    const navigate = useNavigate();

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const features = [
        {
            icon: <Mail size={32} />,
            title: "OTP-Based Authentication",
            desc: "Secure email-based one-time password system with 5-minute expiration"
        },
        {
            icon: <Lock size={32} />,
            title: "AES-256 Encryption",
            desc: "Military-grade encryption for all stored passwords and sensitive data"
        },
        {
            icon: <Key size={32} />,
            title: "Password Generator",
            desc: "Customizable password generation with length, complexity, and mandatory string options"
        },
        {
            icon: <Eye size={32} />,
            title: "Secure Password Manager",
            desc: "Store, view, and manage passwords with user isolation and CRUD operations"
        },
        {
            icon: <Copy size={32} />,
            title: "One-Click Copy",
            desc: "Quick clipboard copying for easy password usage across applications"
        },
        {
            icon: <Smartphone size={32} />,
            title: "Responsive Design",
            desc: "Ultra-modern dark theme with neon accents, optimized for all devices"
        }
    ];

    const techStack = [
        { category: "Backend", items: ["Node.js", "Express.js", "Nodemailer"] },
        { category: "Database", items: ["MySQL", "Foreign Key Constraints"] },
        { category: "Security", items: ["AES-256", "crypto-js", "OTP System"] },
        { category: "Frontend", items: ["HTML5", "CSS3", "Vanilla JavaScript", "Glassmorphism"] }
    ];

    const designFeatures = [
        "Deep navy background (#0a0e27)",
        "Neon accent colors (Cyan, Purple, Pink)",
        "Floating particle background animation",
        "Pulsing radial gradients",
        "Button ripple effects",
        "3D lift on hover",
        "Glassmorphism with backdrop blur",
        "Smooth cubic-bezier transitions",
        "Poppins font family (300-800 weights)",
        "Fully responsive (Desktop, Tablet, Mobile)"
    ];

    return (
        <div className="soul-project">
            {/* Header */}
            <header className="project-header">
                <button onClick={() => navigate('/#projects')} className="back-btn">
                    <ArrowLeft size={20} /> Back to Portfolio
                </button>

                <div className="project-hero">
                    <span className="project-tag">Freelance Project</span>
                    <h1>OTP Password Manager</h1>
                    <h2>Secure Authentication & Password Management System</h2>
                    <p className="tagline">
                        A modern, secure OTP-based authentication system with an integrated password manager
                        featuring AES-256 encryption, ultra-modern dark theme UI with neon accents, and
                        advanced glassmorphism effects for a premium user experience.
                    </p>
                </div>
            </header>

            {/* Overview Section */}
            <section className="section">
                <h3>Project Overview</h3>
                <div className="overview-content">
                    <p>
                        The OTP Password Manager is a comprehensive security solution that combines email-based
                        one-time password authentication with a full-featured password management system. Built
                        with Node.js, Express, and MySQL, it provides enterprise-grade security while maintaining
                        an intuitive, visually stunning user interface.
                    </p>
                    <p>
                        The system features AES-256 encryption for all stored passwords, OTP-based authentication
                        with automatic expiration, and a customizable password generator. The ultra-modern dark
                        theme with neon accents and glassmorphism effects creates a premium user experience that
                        stands out from traditional password managers.
                    </p>
                </div>
            </section>

            {/* Features Section */}
            <section className="section">
                <h3>Key Features</h3>
                <div className="features-grid">
                    {features.map((feature, index) => (
                        <div key={index} className="feature-card">
                            <div className="feature-icon">{feature.icon}</div>
                            <h4>{feature.title}</h4>
                            <p>{feature.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Tech Stack Section */}
            <section className="section">
                <h3>Technology Stack</h3>
                <div className="tech-grid">
                    {techStack.map((tech, index) => (
                        <div key={index} className="tech-category">
                            <h4>{tech.category}</h4>
                            <div className="tech-items">
                                {tech.items.map((item, i) => (
                                    <span key={i} className="tech-badge">{item}</span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Authentication Features */}
            <section className="section">
                <h3>Authentication System</h3>
                <div className="problem-solution">
                    <div className="ps-card solution">
                        <h4>OTP-Based Login</h4>
                        <ul>
                            <li>Email-based one-time password delivery</li>
                            <li>5-minute OTP expiration for security</li>
                            <li>One-time use OTPs (deleted after verification)</li>
                            <li>Gmail SMTP integration with Nodemailer</li>
                            <li>Automatic user registration on first login</li>
                        </ul>
                    </div>
                    <div className="ps-card solution">
                        <h4>Session Management</h4>
                        <ul>
                            <li>Persistent login with localStorage</li>
                            <li>Smart auto-redirect logic based on user state</li>
                            <li>New users redirected to password generator</li>
                            <li>Returning users to password manager</li>
                            <li>Secure session handling</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Password Management */}
            <section className="section">
                <h3>Password Management Features</h3>
                <div className="problem-solution">
                    <div className="ps-card problem">
                        <h4>Password Generator</h4>
                        <ul>
                            <li>Adjustable length (4-20 characters)</li>
                            <li>Uppercase/lowercase letter options</li>
                            <li>Numbers and symbols inclusion</li>
                            <li>Mandatory string requirement</li>
                            <li>Real-time password preview</li>
                            <li>One-click generation</li>
                        </ul>
                    </div>
                    <div className="ps-card solution">
                        <h4>Secure Storage</h4>
                        <ul>
                            <li>AES-256 encryption for all passwords</li>
                            <li>User isolation with foreign key constraints</li>
                            <li>CRUD operations (Create, Read, Delete)</li>
                            <li>Website URL association</li>
                            <li>One-click clipboard copying</li>
                            <li>Automatic decryption on retrieval</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Design Features */}
            <section className="section">
                <h3>Ultra-Modern Design</h3>
                <div className="overview-content">
                    <h4 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Visual Features</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginTop: '1.5rem' }}>
                        {designFeatures.map((feature, index) => (
                            <div key={index} style={{
                                background: 'rgba(0, 245, 255, 0.1)',
                                padding: '1rem',
                                borderRadius: '12px',
                                border: '1px solid rgba(0, 245, 255, 0.2)',
                                color: 'var(--text-secondary)'
                            }}>
                                → {feature}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="section">
                <h3>User Flow</h3>
                <div className="workflow">
                    <div className="workflow-step">
                        <div className="step-number">1</div>
                        <h4>Enter Email</h4>
                        <p>User enters email address to receive OTP via Gmail SMTP</p>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">2</div>
                        <h4>Verify OTP</h4>
                        <p>Enter 6-digit OTP received in email (expires in 5 minutes)</p>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">3</div>
                        <h4>Generate Password</h4>
                        <p>New users create passwords with customizable options and mandatory strings</p>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">4</div>
                        <h4>Manage Passwords</h4>
                        <p>View, copy, and delete saved passwords with AES-256 encryption</p>
                    </div>
                </div>
            </section>

            {/* Security Features */}
            <section className="section">
                <h3>Security Implementation</h3>
                <div className="overview-content">
                    <h4 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Multi-Layer Security</h4>
                    <ul style={{ color: 'var(--text-secondary)', lineHeight: '2', marginLeft: '2rem' }}>
                        <li><strong>AES-256 Encryption:</strong> All passwords encrypted with crypto-js before database storage</li>
                        <li><strong>OTP Expiration:</strong> One-time passwords automatically expire after 5 minutes</li>
                        <li><strong>One-Time Use:</strong> OTPs deleted immediately after successful verification</li>
                        <li><strong>User Isolation:</strong> Foreign key constraints ensure complete data separation</li>
                        <li><strong>Session Security:</strong> Secure localStorage-based session management</li>
                        <li><strong>HTTPS Ready:</strong> Designed for production deployment with SSL/TLS</li>
                        <li><strong>Environment Variables:</strong> Sensitive data stored in .env file</li>
                        <li><strong>SQL Injection Protection:</strong> Parameterized queries throughout</li>
                    </ul>
                </div>
            </section>

            {/* API Endpoints */}
            <section className="section">
                <h3>API Architecture</h3>
                <div className="problem-solution">
                    <div className="ps-card problem">
                        <h4>Authentication Endpoints</h4>
                        <ul>
                            <li>POST /send-otp - Send OTP to email</li>
                            <li>POST /verify-otp - Verify OTP and login</li>
                            <li>Auto user registration on first login</li>
                            <li>Smart redirect based on user state</li>
                        </ul>
                    </div>
                    <div className="ps-card solution">
                        <h4>Password Management Endpoints</h4>
                        <ul>
                            <li>POST /save-password - Encrypt and save password</li>
                            <li>POST /get-passwords - Retrieve user passwords</li>
                            <li>POST /delete-password - Remove saved password</li>
                            <li>Automatic encryption/decryption</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Use Cases */}
            <section className="section">
                <h3>Use Cases</h3>
                <div className="overview-content">
                    <ul style={{ color: 'var(--text-secondary)', lineHeight: '2', marginLeft: '2rem' }}>
                        <li><strong>Personal Password Management:</strong> Securely store and manage all personal account passwords</li>
                        <li><strong>Team Password Sharing:</strong> Share credentials securely within organizations</li>
                        <li><strong>Developer Tools:</strong> Store API keys and development credentials</li>
                        <li><strong>Client Projects:</strong> Manage multiple client account credentials</li>
                        <li><strong>Secure Notes:</strong> Store sensitive information with encryption</li>
                    </ul>
                </div>
            </section>

            {/* Project Links */}
            <section className="section">
                <div className="project-links">
                    <a href="https://github.com/ANJOEJAMES" target="_blank" rel="noreferrer" className="project-btn">
                        <Github size={20} /> View on GitHub
                    </a>
                    <button onClick={() => navigate('/#projects')} className="project-btn secondary">
                        <ArrowLeft size={20} /> Back to Portfolio
                    </button>
                </div>
            </section>

            {/* Footer */}
            <footer className="project-footer">
                <p>© {new Date().getFullYear()} Anjoe James. OTP Password Manager Project.</p>
            </footer>
        </div>
    );
}

export default OtpProject;
